// Données de démonstration du domaine Activités (Accueil, Agir, Apprendre, Liberty Coach).
// Contenus repris tels quels de ../prototype-tc-reprise (Goals, Learn, Community, Agenda, Account, search-data.js).
// Modèles typés et sérialisables : un adaptateur Foundry peut fournir les mêmes formes sans toucher aux composants.

import type { Route } from "./contracts";

/** Images existantes du prototype de reprise ; aucune image nouvelle. */
export const ASSET_BASE = "../prototype-tc-reprise/assets/";
export const asset = (file: string) => ASSET_BASE + file;

/** Horloge de démonstration : jeudi 1er octobre 2026, 18 h 30 (scénario « soir » de la reprise). */
export const DEMO_TODAY = "2026-10-01";
export const DEMO_NOW = new Date(2026, 9, 1, 18, 30);
export const MEMBER_FIRST_NAME = "Alex";

// ── Objectifs ────────────────────────────────────────────────────────────────

export type GoalKind = "target" | "binary";
export interface GoalLink { kind: "Action" | "Leçon" | "Ressource"; title: string }
export interface Goal {
  id: string;
  /** Clé de semaine ; absente pour un objectif en continu. */
  week?: string;
  kind: GoalKind;
  title: string;
  result?: string;
  one?: string;
  many?: string;
  target?: number;
  done?: boolean;
  doneDay?: string;
  repriseOf?: string;
  links?: GoalLink[];
  created?: string;
}
export interface GoalWeek { key: string; start: string; closed: boolean; goals: string[] }
export type EntryMode = "add" | "binary" | "reading";
export interface GoalEntry { id: string; goal: string; day: string; time: string; v: number; mode: EntryMode }

export const GOAL_RULES = { maxGoals: 5, lateCreationUntil: "mercredi", closesOn: "dimanche" } as const;

export const INITIAL_WEEKS: GoalWeek[] = [
  { key: "w40", start: "2026-09-28", closed: false, goals: ["o1", "o2", "o3", "o4"] },
  { key: "w39", start: "2026-09-21", closed: true, goals: ["p1", "p2", "p3"] },
  { key: "w38", start: "2026-09-14", closed: true, goals: ["q1", "q2"] },
];

export const INITIAL_GOALS: Record<string, Goal> = {
  o1: { id: "o1", week: "w40", kind: "target", title: "Appels de relance", one: "appel", many: "appels", target: 10, repriseOf: "p1",
    links: [{ kind: "Action", title: "Relancer les personnes venues à la séance découverte" }, { kind: "Leçon", title: "[Titre de la leçon]" }] },
  o2: { id: "o2", week: "w40", kind: "target", title: "Séances découverte réalisées", one: "séance", many: "séances", target: 3,
    links: [{ kind: "Action", title: "Lister les créneaux calmes du club" }] },
  o3: { id: "o3", week: "w40", kind: "binary", title: "Publier l’offre small group", done: false, links: [{ kind: "Ressource", title: "[Titre de la ressource]" }] },
  o4: { id: "o4", week: "w40", kind: "target", title: "Nouveaux contacts", one: "contact", many: "contacts", target: 5 },
  p1: { id: "p1", week: "w39", kind: "target", title: "Appels de relance", one: "appel", many: "appels", target: 10 },
  p2: { id: "p2", week: "w39", kind: "binary", title: "Préparer le flyer small group", done: true, doneDay: "2026-09-26" },
  p3: { id: "p3", week: "w39", kind: "target", title: "Nouveaux contacts", one: "contact", many: "contacts", target: 4 },
  q1: { id: "q1", week: "w38", kind: "target", title: "Appels de relance", one: "appel", many: "appels", target: 8 },
  q2: { id: "q2", week: "w38", kind: "target", title: "Séances découverte réalisées", one: "séance", many: "séances", target: 2 },
  c1: { id: "c1", kind: "target", title: "Clients suivis", one: "client", many: "clients" },
};

export const INITIAL_ENTRIES: GoalEntry[] = [
  { id: "e1", goal: "o1", day: "2026-09-29", time: "19:05", v: 2, mode: "add" },
  { id: "e2", goal: "o1", day: "2026-09-30", time: "18:40", v: 3, mode: "add" },
  { id: "e3", goal: "o1", day: "2026-10-01", time: "12:10", v: 1, mode: "add" },
  { id: "e4", goal: "o2", day: "2026-09-30", time: "20:15", v: 1, mode: "add" },
  { id: "e5", goal: "o4", day: "2026-09-28", time: "17:30", v: 2, mode: "add" },
  { id: "e6", goal: "o4", day: "2026-09-30", time: "19:00", v: 3, mode: "add" },
  { id: "e7", goal: "o4", day: "2026-10-01", time: "10:05", v: 2, mode: "add" },
  { id: "e8", goal: "p1", day: "2026-09-23", time: "18:20", v: 3, mode: "add" },
  { id: "e9", goal: "p1", day: "2026-09-25", time: "19:10", v: 4, mode: "add" },
  { id: "e10", goal: "p2", day: "2026-09-26", time: "11:00", v: 1, mode: "binary" },
  { id: "e11", goal: "p3", day: "2026-09-24", time: "17:45", v: 2, mode: "add" },
  { id: "e12", goal: "q1", day: "2026-09-16", time: "18:00", v: 5, mode: "add" },
  { id: "e13", goal: "q2", day: "2026-09-17", time: "20:30", v: 2, mode: "add" },
  { id: "e14", goal: "c1", day: "2026-09-21", time: "09:00", v: 13, mode: "reading" },
  { id: "e15", goal: "c1", day: "2026-09-28", time: "09:00", v: 14, mode: "reading" },
];

// ── Tâches et routines ───────────────────────────────────────────────────────

export type RoutineFreq = "daily" | "workdays" | "weekly";
export interface Routine {
  id: string;
  title: string;
  rhythm: string;
  short: string;
  /** Réalisations attendues par semaine. */
  n: number;
  /** Jours prescrits (0 = dimanche) ; null = jour libre. */
  days: number[] | null;
  /** Jours déjà cochés cette semaine. */
  past: string[];
  origin: string;
  goal?: string;
}
export interface ActionItem { id: string; title: string; meta: string; late?: boolean; due?: string; goal?: string; origin?: string }

export const INITIAL_ROUTINES: Routine[] = [
  { id: "r1", title: "Bloc prospection au club", rhythm: "Mardi et jeudi, 20 min", short: "mar. et jeu.", n: 2, days: [2, 4], past: ["2026-09-29"], origin: "Prescrite en séance coaching · [Nom du coach]" },
  { id: "r2", title: "Publier un post sur ton activité", rhythm: "2 fois par semaine, jour libre", short: "jour libre", n: 2, days: null, past: ["2026-09-28"], origin: "Choisie par toi" },
];

export const INITIAL_ACTIONS: ActionItem[] = [
  { id: "a2", title: "Fixer le prix de ton carnet de séances", meta: "Échéance hier · Bilan accepté", late: true, origin: "Bilan accepté" },
  { id: "a3", title: "Lister les créneaux calmes du club", meta: "14 h 00–14 h 30 · Consultation avec Arnaud", goal: "o2", origin: "Consultation avec Arnaud" },
  { id: "a1", title: "Relancer les personnes venues à la séance découverte", meta: "Séance coaching · [Nom du coach]", goal: "o1", origin: "Séance coaching · [Nom du coach]" },
  { id: "a4", title: "Noter tes questions pour la prochaine séance", meta: "Initiative", origin: "Initiative" },
];
export const ACTIONS_MORE_LABEL = "2 faites aujourd’hui · 3 à venir · 3 sans date";

// ── Apprendre ────────────────────────────────────────────────────────────────

export type LevelAccess = "included" | "not_included" | "soon";
export interface Level { id: string; title: string; subtitle: string; access: LevelAccess; steps: string[]; stepCount?: number }
export interface Step { id: string; level: string; n: number; title: string; lessons: string[]; prereq?: string }
export type LessonFormat = "text" | "video" | "file" | "support" | null;
export interface LessonFile { name: string; meta: string }
export interface LessonPart { h: string; t: string }
export interface Lesson {
  id: string;
  step: string;
  title: string;
  format: LessonFormat;
  dur?: string;
  durSec?: number;
  theme: string;
  summary?: string;
  excerpt?: string[];
  files?: LessonFile[];
  cover?: "sequence" | "questions";
  soon?: boolean;
}
export type ResourceCollection = "res" | "replay" | "tool";
export interface Resource {
  id: string;
  coll: ResourceCollection;
  title: string;
  format: LessonFormat;
  meta: string;
  theme: string;
  summary?: string;
  excerpt?: string[];
  files?: LessonFile[];
  parts?: LessonPart[];
  durSec?: number;
  ext?: boolean;
  replayUnavailable?: boolean;
}
export interface LearnProgress { done: Record<string, string>; pos: Record<string, number>; last: string }

export const LEVELS: Level[] = [
  { id: "L1", title: "Niveau 1", subtitle: "Poser les bases de ton activité", access: "included", steps: ["E1", "E2", "E3"] },
  { id: "L2", title: "Niveau 2", subtitle: "Développer ta clientèle", access: "not_included", steps: [], stepCount: 3 },
  { id: "L3", title: "Niveau 3", subtitle: "Structurer ton activité", access: "soon", steps: [] },
];

export const STEPS: Record<string, Step> = {
  E1: { id: "E1", level: "L1", n: 1, title: "Clarifier ton offre", lessons: ["l5", "l6"] },
  E2: { id: "E2", level: "L1", n: 2, title: "Réussir ta séance découverte", lessons: ["l1", "l2", "l3", "l4"] },
  E3: { id: "E3", level: "L1", n: 3, title: "Fidéliser tes premiers clients", lessons: ["l7", "l8", "l9", "l10"], prereq: "E2" },
};

export const LESSONS: Record<string, Lesson> = {
  l5: { id: "l5", step: "E1", title: "Définir ton client idéal", format: "text", dur: "7 min", theme: "Marketing",
    excerpt: ["Commence par décrire une personne précise plutôt qu’un public large : son objectif principal, ce qu’elle a déjà essayé, ce qui l’a fait renoncer.", "Note ensuite ce qui la freine aujourd’hui. C’est souvent là que ton accompagnement fait la différence."],
    summary: "Décrire la personne que tu aides le mieux, ses objectifs et ce qui la freine." },
  l6: { id: "l6", step: "E1", title: "Ton pitch en 30 secondes", format: "video", dur: "6 min", durSec: 360, theme: "Marketing", summary: "Présenter ce que tu fais en une phrase claire, adaptée au club." },
  l1: { id: "l1", step: "E2", title: "Structurer ta séance découverte", format: "video", cover: "sequence", dur: "12 min", durSec: 720, theme: "Vente", summary: "Les temps d’une séance découverte, de l’accueil à la proposition." },
  l2: { id: "l2", step: "E2", title: "Poser les bonnes questions", format: "video", cover: "questions", dur: "9 min", durSec: 540, theme: "Vente",
    summary: "Des questions pour comprendre l’objectif de la personne et ce qui l’a empêchée d’avancer.", files: [{ name: "Questions clés", meta: "PDF · [taille]" }] },
  l3: { id: "l3", step: "E2", title: "Présenter ton offre sans pression", format: "text", dur: "6 min", theme: "Vente",
    excerpt: ["Reprends les mots de la personne : son objectif, ce qui l’a bloquée, ce qu’elle attend d’un accompagnement.", "Présente ensuite une seule proposition adaptée, avec son déroulé et son prix, puis laisse-lui le temps de répondre."],
    summary: "Relier ton offre à ce que la personne t’a dit, puis laisser la décision lui appartenir.", files: [{ name: "Trame de présentation", meta: "PDF · [taille]" }] },
  l4: { id: "l4", step: "E2", title: "Relancer après la séance", format: null, theme: "Vente", soon: true },
  l7: { id: "l7", step: "E3", title: "Mettre en place un suivi", format: "text", dur: "5 min", theme: "Opérationnel",
    excerpt: ["Choisis un moment fixe pour faire le point avec chaque client, par exemple toutes les deux semaines.", "Garde une trace courte de ce qui a été dit pour reprendre le fil la fois suivante."],
    summary: "Choisir un rythme de suivi simple et tenable." },
  l8: { id: "l8", step: "E3", title: "Demander un retour", format: "video", dur: "7 min", durSec: 420, theme: "Vente", summary: "Recueillir un avis honnête sur l’accompagnement." },
  l9: { id: "l9", step: "E3", title: "Proposer un renouvellement", format: "video", dur: "8 min", durSec: 480, theme: "Vente", summary: "Préparer la suite avec la personne, au bon moment." },
  l10: { id: "l10", step: "E3", title: "Obtenir des recommandations", format: "text", dur: "5 min", theme: "Acquisition",
    excerpt: ["Demande une recommandation au moment où ton client constate lui-même un progrès.", "Propose-lui une phrase simple qu’il peut transmettre, sans insister."],
    summary: "Demander une recommandation sans gêne." },
};

export const RESOURCES: Resource[] = [
  { id: "r1", coll: "res", title: "Questions de découverte", format: "file", meta: "Fiche PDF · 2 pages", theme: "Vente",
    summary: "Une fiche à garder sous la main pendant tes séances découverte.", files: [{ name: "Questions de découverte", meta: "PDF · [taille]" }] },
  { id: "r2", coll: "res", title: "Modèle de message de relance", format: "text", meta: "Texte · 3 min", theme: "Acquisition",
    excerpt: ["Bonjour [Prénom], merci pour notre échange de [jour]. Tu m’avais parlé de [objectif].", "Si tu veux en reparler, je te propose [créneau]. Sinon, aucun souci : je reste disponible."],
    summary: "Un modèle court à adapter à chaque personne." },
  { id: "rp1", coll: "replay", title: "Atelier tarifs", format: "video", meta: "Replay · 58 min · live du 17 septembre", durSec: 3480, theme: "Vente",
    summary: "Enregistrement d’un atelier collectif consacré à la construction de tes tarifs." },
  { id: "mc1", coll: "replay", title: "Fixer et annoncer tes tarifs", format: "support", replayUnavailable: true, meta: "Support écrit et modèle de grille", theme: "Vente",
    summary: "Construire une grille de tarifs claire et la présenter sans te justifier.",
    parts: [
      { h: "Partir de ce que tu proposes", t: "Liste les accompagnements que tu proposes réellement. Une ligne par offre, sans en ajouter pour remplir la grille." },
      { h: "Relier chaque tarif à un contenu", t: "Pour chaque offre, note ce que la personne obtient : format, durée, nombre de séances. Le tarif se lit à côté de ce contenu, jamais seul." },
      { h: "Annoncer sans te justifier", t: "Rappelle l’objectif de la personne, présente l’offre adaptée et son tarif en une phrase, puis laisse-lui le temps de répondre." },
    ] },
  { id: "t1", coll: "tool", title: "Tableur de suivi clients", format: null, ext: true, meta: "Outil externe · tableur", theme: "Opérationnel" },
  { id: "t2", coll: "tool", title: "Calculateur de rentabilité", format: null, ext: true, meta: "Outil externe · calculateur", theme: "Entreprise" },
];

export const RESOURCE_COLLECTIONS: { value: "all" | ResourceCollection; label: string }[] = [
  { value: "all", label: "Tout" },
  { value: "res", label: "Ressources" },
  { value: "replay", label: "Replays" },
  { value: "tool", label: "Outils" },
];

export const INITIAL_LEARN_PROGRESS: LearnProgress = { done: { l5: "2026-09-12", l6: "2026-09-15", l1: "2026-09-24" }, pos: { l2: 12 }, last: "l2" };

/** Visuels de leçon existants (reprise) : séquence et questions. */
export const LESSON_COVERS: Record<NonNullable<Lesson["cover"]>, string> = {
  sequence: asset("tc-learn-sequence.svg"),
  questions: asset("tc-learn-questions.svg"),
};

// ── Actualités (espace « news » de la Communauté) ────────────────────────────

export interface NewsItem { id: string; title: string; text: string; time: string; important?: boolean }
export const NEWS: NewsItem[] = [
  { id: "n1", title: "Tes résultats, en une minute", time: "il y a 2 h",
    text: "Depuis Agir, « Renseigner » sur un objectif enregistre tes résultats ; tes routines se cochent dans Tâches et routines du jour." },
  { id: "n2", title: "Une routine peut accompagner un objectif", time: "hier",
    text: "À la création d’un objectif, « Ajouter une routine » est facultatif. La routine se coche à part et ne fait pas progresser le résultat." },
  { id: "p6", title: "Deux replays ajoutés dans Apprendre", time: "mar.",
    text: "Cette semaine : deux replays ajoutés dans Apprendre, en Vente et en Marketing. Le hot seat de mardi a encore des places." },
  { id: "n3", title: "Ton espace Liberty Coach", time: "lun.",
    text: "Les liens préparés par l’équipe s’ouvrent dans Google Sheets, depuis Liberty Coach." },
];

// ── Agenda (événements de la reprise) ────────────────────────────────────────

export type AgendaType = "tg" | "consult" | "live" | "tour" | "atelier";
export type AgendaAccess = "included" | "booked" | "register" | "buy" | "book" | "none";
export interface AgendaEvent {
  id: string;
  type: AgendaType;
  title: string;
  date: string;
  start: string;
  end: string;
  place: "visio" | "site";
  placeLabel?: string;
  access?: AgendaAccess;
  desc: string;
  org?: string;
  whereSub?: string;
  /** Route du domaine qui porte la suite (réservation, fil, séance). */
  route: Route;
}
export const AGENDA_SOURCES: { id: AgendaType; name: string; color: string }[] = [
  { id: "tg", name: "Team Genius", color: "violet" },
  { id: "consult", name: "Consultations", color: "rose" },
  { id: "live", name: "Lives", color: "blue" },
  { id: "tour", name: "Tournée des coachs", color: "amber" },
  { id: "atelier", name: "Ateliers", color: "teal" },
];
export const AGENDA_ACCESS_LABEL: Record<AgendaAccess, string> = {
  included: "Inclus", booked: "Réservé", register: "Inscription ouverte", buy: "Payant", book: "À réserver", none: "Date à venir",
};
export const AGENDA_EVENTS: AgendaEvent[] = [
  { id: "e1", type: "tg", title: "Remplir les créneaux creux du club", date: "2026-10-05", start: "16:10", end: "17:00", place: "visio", access: "included", route: "accompagnement",
    whereSub: "Visio ouverte de 16 h 10 à 16 h 30 depuis la fiche de la séance",
    desc: "Repère tes créneaux les moins remplis et choisis une offre simple pour les animer. Viens avec ton planning de la semaine." },
  { id: "e2", type: "consult", title: "Hot seat Arnaud", date: "2026-10-06", start: "18:00", end: "19:30", place: "visio", access: "booked", org: "Arnaud", route: "accompagnement",
    desc: "Séance collective en visio : tu présentes ta situation, le groupe et Arnaud t’aident à trouver la prochaine étape." },
  { id: "e3", type: "live", title: "Relancer après une séance découverte", date: "2026-10-08", start: "19:00", end: "20:00", place: "visio", access: "included", org: "Équipe Team Coaching", route: "community",
    whereSub: "Lien communiqué avant le live",
    desc: "Live du mois : on part de vos cas. Dépose ta question en réponse à la publication avant mercredi." },
  { id: "e5", type: "consult", title: "Hot seat Arnaud", date: "2026-10-13", start: "18:00", end: "19:30", place: "visio", access: "book", org: "Arnaud", route: "accompagnement",
    desc: "Même format que le hot seat du 6 octobre, sur le créneau du mardi proposé dans Consultations." },
  { id: "e4", type: "tour", title: "Tournée des coachs · Lyon", date: "2026-10-15", start: "19:00", end: "21:30", place: "site", placeLabel: "Lyon", access: "register", org: "Équipe Team Coaching", route: "accompagnement",
    whereSub: "Lieu exact communiqué par l’organisateur aux inscrits",
    desc: "Une soirée entre coachs de la région : retours d’expérience, questions à l’équipe et temps d’échange." },
  { id: "e6", type: "atelier", title: "Ta séance découverte en pratique", date: "2026-10-21", start: "12:30", end: "14:00", place: "visio", access: "buy", org: "Équipe Team Coaching", route: "shop",
    desc: "Répète ta séance découverte en petit groupe et repars avec une trame testée." },
  { id: "p1", type: "consult", title: "Consultation avec Arnaud", date: "2026-09-28", start: "11:00", end: "11:30", place: "visio", org: "Arnaud", route: "accompagnement",
    desc: "Rendez-vous individuel passé. L’action qui en est issue est dans tes Actions." },
  { id: "p3", type: "consult", title: "Consultation Business coach", date: "2026-09-24", start: "14:00", end: "14:45", place: "visio", route: "accompagnement",
    desc: "Rendez-vous individuel passé." },
];

// ── Accès rapides (tuiles de l’Accueil, visuels existants) ───────────────────

export interface QuickAccess { id: string; title: string; sub: string; image: string; fit: "cover" | "contain"; route: Route }
export const QUICK_ACCESS: QuickAccess[] = [
  { id: "formation", title: "Formation", sub: "Mon parcours, à mon rythme", image: "./assets/tc-da-formation-0810.png", fit: "cover", route: "learn" },
  { id: "coaching", title: "Coaching", sub: "Mes rendez-vous et accompagnements", image: "./assets/tc-da-coaching-0810.png", fit: "cover", route: "accompagnement" },
  { id: "liberty", title: "Liberty Coach", sub: "Mes documents et mon suivi", image: "./assets/tc-da-liberty-0810.png", fit: "cover", route: "liberty" },
  { id: "community", title: "Communauté", sub: "Le fil et mes espaces", image: "./assets/tc-da-community-0810.png", fit: "cover", route: "community" },
];

// ── Liberty Coach ────────────────────────────────────────────────────────────

export interface LibertyLink { id: string; title: string; kind: "Google Sheets" | "Google Docs"; sub: string; set: boolean }
export const LIBERTY_LINKS: LibertyLink[] = [
  { id: "suivi", title: "Mon suivi Liberty Coach", kind: "Google Sheets", sub: "préparé pour toi", set: true },
  { id: "commissions", title: "Mes commissions", kind: "Google Sheets", sub: "Lien pas encore renseigné par l’équipe", set: false },
];
export const LIBERTY_RESOURCES: LibertyLink[] = [
  { id: "guide", title: "Comprendre ton suivi Liberty Coach", kind: "Google Docs", sub: "à lire avant de commencer", set: true },
  { id: "modele", title: "Modèle de point hebdomadaire", kind: "Google Sheets", sub: "à copier dans ton espace", set: true },
  { id: "faq", title: "Questions fréquentes Liberty Coach", kind: "Google Docs", sub: "réponses courantes", set: true },
];
/** Liberty Coach categories. Only Vente has content today; the others render a sober empty state (no offer, no price). */
export const LIBERTY_CATEGORIES = [
  { id: "vente", label: "Vente" },
  { id: "sport", label: "Programmes sportifs" },
  { id: "compta", label: "Comptabilité" },
  { id: "social", label: "Réseaux sociaux" },
] as const;
export type LibertyCategory = (typeof LIBERTY_CATEGORIES)[number]["id"];
export const LIBERTY_PARTICIPANTS = [
  { id: "me", name: "Alex Moreau" },
  { id: "team", name: "Équipe Team Coaching", avatar: asset("favicon-tc.svg") },
];
